import { useMemo, useState } from 'react'
import { useParams, Link, useNavigate, useSearch } from '@tanstack/react-router'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Badge } from './ui/badge'
import { Checkbox } from './ui/checkbox'
import { mdiChevronLeft, mdiChevronRight, mdiCog, mdiEyeOutline, mdiMagnify, mdiPlus } from '@mdi/js'
import { AppIcon } from './ui/icon'
import { useAssetTypes } from '../hooks/useQueries'
import { AssetTypeTile } from './assets/AssetTypeTile'
import { useBreadcrumbs } from '../hooks/useBreadcrumbs'
import { useTranslation } from 'react-i18next'
import { formatCreated } from '../lib/format'
import { getLocalizedName } from '../lib/localization'
import { PageHeader } from './shared/PageHeader'
import { SECTION_ICONS } from '../lib/section-icons'
import { Can } from '../contexts/PermissionsContext'
import { CreateAssetTypeDialog } from './assets/CreateAssetTypeDialog'
import { AssetTypeHierarchy, type AssetTypeTreeMode } from './assets/AssetTypeHierarchy'

const PAGE_SIZE = 20

export function AssetTypesPage() {
  const { org } = useParams({ strict: false })
  const navigate = useNavigate()
  const { t, i18n } = useTranslation('types')
  const [isCreateOpen, setIsCreateOpen] = useState(false)

  // Read search params from URL
  const search = useSearch({ strict: false }) as { q?: string; page?: string; view?: 'tree'; tree?: 'children' }
  const query = search.q || ''
  const view = search.view === 'tree' ? 'tree' : 'list'
  const treeMode: AssetTypeTreeMode = search.tree === 'children' ? 'children' : 'inheritance'
  const currentPage = Math.max(1, parseInt(search.page || '1', 10) || 1)

  const { data: allAssetTypes = [], isLoading, error } = useAssetTypes(org || 'demo', true)
  // Archived types are hidden behind a toggle (spec 2026-10-11-28).
  const [showArchived, setShowArchived] = useState(false)
  const archivedCount = useMemo(
    () => allAssetTypes.filter((at: any) => at.archived_at).length,
    [allAssetTypes]
  )
  const assetTypes = useMemo(
    () => (showArchived ? allAssetTypes : allAssetTypes.filter((at: any) => !at.archived_at)),
    [allAssetTypes, showArchived]
  )

  // Helper function to handle multilingual asset type names
  const getAssetTypeName = (assetType: any) => {
    if (!assetType?.name) return assetType?.code || t('asset_types.unnamed')

    // Handle multilingual names
    if (typeof assetType.name === 'object' && assetType.name !== null) {
      const names = assetType.name as Record<string, string>
      return getLocalizedName(names, i18n.language) || assetType.code || t('asset_types.unnamed')
    }

    return assetType.name || assetType.code || t('asset_types.unnamed')
  }

  // Helper function to handle multilingual descriptions
  const getAssetTypeDescription = (assetType: any) => {
    if (!assetType?.description) return ''

    // Handle multilingual descriptions
    if (typeof assetType.description === 'object' && assetType.description !== null) {
      const descriptions = assetType.description as Record<string, string>
      return getLocalizedName(descriptions, i18n.language)
    }

    return assetType.description || ''
  }

  // A type matches on its code or on its name in ANY language, so `?q=Unit`
  // finds the Lot in a French session too (issue #1468).
  const matchesQuery = (at: any, q: string) => {
    if ((at.code || '').toLowerCase().includes(q)) return true
    const names = at.name && typeof at.name === 'object' ? Object.values(at.name as Record<string, string>) : [getAssetTypeName(at)]
    return names.some((n) => typeof n === 'string' && n.toLowerCase().includes(q))
  }

  // Filter by search query
  const filtered = useMemo(() => {
    if (!query) return assetTypes
    const q = query.toLowerCase()
    return assetTypes.filter((at: any) => matchesQuery(at, q))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetTypes, query])

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const safePage = Math.min(currentPage, totalPages)
  const paginated = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE)

  const updateSearch = (params: { q?: string; page?: string; view?: string; tree?: string }) => {
    navigate({
      to: '/$org/asset-types',
      params: { org: org || 'demo' },
      search: (prev: any) => {
        const next = { ...prev, ...params }
        if (!next.q) delete next.q
        if (!next.page || next.page === '1') delete next.page
        if (next.view !== 'tree') delete next.view
        if (next.tree !== 'children') delete next.tree
        return next
      },
      replace: true,
    })
  }

  // Set breadcrumbs
  const breadcrumbs = useMemo(() => [
    { title: t('breadcrumb.home'), path: `/${org}` },
    { title: t('breadcrumb.org_settings'), path: `/${org}/settings` },
    { title: t('asset_types.title') }
  ], [org, t])

  useBreadcrumbs(breadcrumbs)

  return (
    <div className="container mx-auto py-8 max-w-6xl">
      <div className="bg-card rounded-lg shadow-lg">
        <div className="px-6 py-4 border-b border-border">
          <PageHeader
            icon={SECTION_ICONS.assetTypes}
            title={t('asset_types.title')}
            description={t('asset_types.subtitle')}
            actions={
              <Can perm="configuration.write">
                <Button
                  onClick={() => setIsCreateOpen(true)}
                  aria-label={t('asset_types.create')}
                  title={t('asset_types.create')}
                  data-testid="create-asset-type-button"
                >
                  <AppIcon path={mdiPlus} size="sm" className="md:mr-1" />
                  <span className="hidden md:inline">{t('asset_types.create')}</span>
                </Button>
              </Can>
            }
          />
        </div>

        <div className="p-6">
          <div className="space-y-6">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h2 className="text-lg font-medium text-foreground">{t('asset_types.title')}</h2>
                <p className="text-muted-foreground text-sm mt-1">
                  {t('asset_types.list_subtitle')}
                </p>
              </div>
              <div className="flex items-center gap-2">
              <div role="group" aria-label={t('asset_types.view_aria')} className="flex rounded-md border p-0.5">
                {(['list', 'tree'] as const).map((v) => (
                  <Button
                    key={v}
                    variant={view === v ? 'secondary' : 'ghost'}
                    size="sm"
                    aria-pressed={view === v}
                    onClick={() => updateSearch({ view: v, page: '1' })}
                    data-testid={`asset-types-view-${v}`}
                  >
                    {t(`asset_types.view_${v}`)}
                  </Button>
                ))}
              </div>
              <label className="flex items-center gap-2 text-sm text-muted-foreground whitespace-nowrap">
                <Checkbox
                  checked={showArchived}
                  onCheckedChange={(v) => setShowArchived(v === true)}
                  data-testid="asset-types-show-archived"
                />
                {t('asset_types.show_archived')}
                {/* The tree only lists types that hold something, so the toggle can
                    look dead there: the count says how many types it governs (#1721). */}
                {archivedCount > 0 && (
                  <span data-testid="asset-types-archived-count">({archivedCount})</span>
                )}
              </label>
              <div className="relative w-72">
                <AppIcon path={mdiMagnify} size="sm" className="absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  data-testid="asset-types-search"
                  placeholder={t('asset_types.search_placeholder')}
                  value={query}
                  onChange={(e) => updateSearch({ q: e.target.value, page: '1' })}
                  className="pl-9"
                />
              </div>
              </div>
            </div>

            {view === 'tree' ? (
              <div className="rounded-md border border-border">
                <AssetTypeHierarchy
                  org={org || 'demo'}
                  types={assetTypes as any[]}
                  mode={treeMode}
                  onModeChange={(m) => updateSearch({ tree: m })}
                  getName={getAssetTypeName}
                  query={query}
                  matches={matchesQuery}
                />
              </div>
            ) : (

            <div className="bg-card shadow overflow-hidden rounded-md border border-border">
              <table className="min-w-full divide-y divide-border">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-6 py-3 text-left text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                      {t('asset_types.column_name')}
                    </th>
                    <th className="px-6 py-3 text-left text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                      {t('asset_types.column_code')}
                    </th>
                    <th className="px-6 py-3 text-left text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                      {t('asset_types.column_created')}
                    </th>
                    <th className="px-6 py-3 text-left text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                      {t('asset_types.column_actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-card divide-y divide-border">
                  {isLoading ? (
                    <tr>
                      <td colSpan={4} className="px-6 py-12 text-center">
                        <div className="flex items-center justify-center">
                          <div className="motion-safe:animate-spin rounded-full h-6 w-6 border-b-2 border-primary mr-3"></div>
                          <span className="text-muted-foreground">{t('asset_types.loading')}</span>
                        </div>
                      </td>
                    </tr>
                  ) : error ? (
                    <tr>
                      <td colSpan={4} className="px-6 py-12 text-center">
                        <div className="flex flex-col items-center justify-center">
                          <svg className="h-12 w-12 text-destructive mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <p className="text-sm text-destructive mb-2">
                            {t('asset_types.error')}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {(error as any)?.title || (error as any)?.message || t('asset_types.unknown_error')}
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : paginated.length > 0 ? (
                    paginated.map((assetType: any) => (
                      <tr key={assetType.uid} className="hover:bg-muted/50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                          <Link
                            to="/$org/asset-types/$assetTypeUid"
                            params={{ org: org || 'demo', assetTypeUid: assetType.uid }}
                            className="font-medium flex items-center gap-2 w-fit hover:underline underline-offset-2"
                            data-testid={`asset-type-name-${assetType.uid}`}
                          >
                            <AssetTypeTile type={assetType} size="sm" />
                            <span>{getAssetTypeName(assetType)}</span>
                            {(assetType as any).archived_at && (
                              <Badge variant="secondary" data-testid={`asset-type-archived-${assetType.uid}`}>{t('asset_types.archived_badge')}</Badge>
                            )}
                          </Link>
                          {getAssetTypeDescription(assetType) && (
                            <p className="text-xs text-muted-foreground mt-1 truncate max-w-xs">
                              {getAssetTypeDescription(assetType)}
                            </p>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                          <code className="text-xs bg-muted px-2 py-1 rounded">
                            {assetType.code}
                          </code>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                          {formatCreated(assetType.created_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-2">
                            <Link to="/$org/assets" params={{ org: org || 'demo' }} search={{ showBuildingGroups: true, showBuildings: true, q: '', asset_type_uid: assetType.uid } as any}>
                              <Button
                                variant="ghost"
                                size="sm"
                                data-testid={`view-assets-${assetType.uid}`}
                              >
                                <AppIcon path={mdiEyeOutline} size="sm" />
                              </Button>
                            </Link>
                            <Link to={`/${org}/asset-types/${assetType.uid}` as any}>
                              <Button
                                variant="ghost"
                                size="sm"
                                data-testid={`manage-${assetType.uid}`}
                              >
                                <AppIcon path={mdiCog} size="sm" />
                              </Button>
                            </Link>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : query ? (
                    <tr>
                      <td colSpan={4} className="px-6 py-12 text-center">
                        <p className="text-sm text-muted-foreground">{t('asset_types.no_results')}</p>
                      </td>
                    </tr>
                  ) : (
                    <tr>
                      <td colSpan={4} className="px-6 py-12 text-center">
                        <div className="flex flex-col items-center justify-center">
                          <svg className="h-12 w-12 text-muted-foreground mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                          </svg>
                          <p className="text-sm text-muted-foreground mb-4">
                            {t('asset_types.empty_title')}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {t('asset_types.empty_description')}
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              {/* Pagination */}
              {filtered.length > PAGE_SIZE && (
                <div className="flex items-center justify-between px-6 py-3 border-t border-border">
                  <p className="text-sm text-muted-foreground">
                    {t('asset_types.showing_count', {
                      from: (safePage - 1) * PAGE_SIZE + 1,
                      to: Math.min(safePage * PAGE_SIZE, filtered.length),
                      total: filtered.length,
                    })}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={safePage <= 1}
                      onClick={() => updateSearch({ page: String(safePage - 1) })}
                      data-testid="asset-types-prev-page"
                    >
                      <AppIcon path={mdiChevronLeft} size="sm" tone="muted" />
                    </Button>
                    <span className="text-sm text-muted-foreground">
                      {safePage} / {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={safePage >= totalPages}
                      onClick={() => updateSearch({ page: String(safePage + 1) })}
                      data-testid="asset-types-next-page"
                    >
                      <AppIcon path={mdiChevronRight} size="sm" tone="muted" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
            )}
          </div>
        </div>
      </div>

      <CreateAssetTypeDialog
        org={org || 'demo'}
        open={isCreateOpen}
        onOpenChange={setIsCreateOpen}
        assetTypes={assetTypes}
      />
    </div>
  )
}
